/**
 * Utility functions for working with mock data across the application
 */
import mockData from '../../test-mocks/mocks.json';
import { EventLog, ServerGroup, RateLimitRule, SecurityPolicy, ScannedMockItem } from '@/types';

/**
 * Determines if we should use mock data based on environment
 * In development/local environment, use mock data
 * In production and test environments, use API data
 */
export const shouldUseMockData = () => {
  if (process.env.NODE_ENV === 'test') {
    return false;
  }
  return process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
};

export const getAiManagementMockData = () => {
  return {
    timeline: mockData.aiManagementData.timeline,
    stats: mockData.aiManagementData.stats,
    topx: mockData.aiManagementData.topx,
    ai_tools_stats: mockData.aiManagementData.ai_tools_stats,
  };
};

export const getAiCrawlersMockData = () => {
  return mockData.aiCrawlers;
};

export const getDashboardMockData = () => {
  return {
    timeline: mockData.dashboardData.timeline,
    stats: mockData.dashboardData.stats,
    topx: mockData.dashboardData.topx,
  };
};

export const getQuarantinedMockData = () => {
  return mockData.quarantinedData;
};

export const getDynamicRulesMockData = () => {
  return mockData.dynamicRules;
};

export const getActionsMockData = () => {
  return mockData.actions;
};

export const getAllMockData = () => {
  return mockData;
};

const DEFAULT_FILTERS_QUERY = 'timestamp>="2025-01-01", branch=main';
export const getEventsLogMockData = (
  count = 50
): {
  data: EventLog[];
  serverGroups: ServerGroup[];
  filtersQuery: string;
} => {
  const events = Array.isArray(mockData.eventsLogData)
    ? (mockData.eventsLogData as EventLog[])
    : [];
  const serverGroups = Array.isArray(mockData.serverGroups?.items)
    ? (mockData.serverGroups.items as ServerGroup[])
    : [];
  return {
    data: events.slice(0, count),
    serverGroups,
    filtersQuery: DEFAULT_FILTERS_QUERY,
  };
};

export const getSslLoadBalancersMockData = () => {
  return mockData.sslLoadBalancers?.items || [];
};

export const getSslLoadBalancersRegionsMockData = () => {
  return mockData.sslLoadBalancersRegions || {};
};

export const getCertificatesMockData = () => {
  return mockData.certificates?.items || [];
};

export const getRateLimitRulesMockData = (): { items: RateLimitRule[] } => {
  return (mockData.rateLimitRules || { items: [] }) as unknown as { items: RateLimitRule[] };
};

export const getSecurityPoliciesMockData = (): { items: SecurityPolicy[] } => {
  return (mockData.securityPolicies || { items: [] }) as unknown as { items: SecurityPolicy[] };
};

export const getMockTagData = (
  tagName: string
): {
  type: string[];
  embeddings: Array<{
    document: { title: string; type: string };
    entity: { id: string; name: string };
    field: { id: string };
  }>;
} => {
  const normalize = (text: string): string => {
    return (text || '')
      .trim()
      .replace(/[^\w]|_/g, '-')
      .toLowerCase();
  };

  const docMapping: Record<string, { type: string; title: string }> = {
    rateLimitRules: { type: 'rate-limit-rules', title: 'Rate Limit Rules' },
    dynamicRules: { type: 'dynamic-rules', title: 'Dynamic Rules' },
    securityPolicies: { type: 'security-policies', title: 'Security Policies' },
    aclProfiles: { type: 'acl-profiles', title: 'ACL Profiles' },
    contentFilterProfiles: { type: 'content-filter-profiles', title: 'Content Filter Profiles' },
    contentFilterRules: { type: 'content-filter-rules', title: 'Content Filter Rules' },
    edgeFunctions: { type: 'edge-functions', title: 'Edge Functions' },
    flowControlPolicies: { type: 'flow-control-policies', title: 'Flow Control Policies' },
  };

  const embeddings: Array<{
    document: { title: string; type: string };
    entity: { id: string; name: string };
    field: { id: string };
  }> = [];

  const mockDataTyped = mockData as unknown as Record<string, { items: ScannedMockItem[] }>;

  for (const [key, docInfo] of Object.entries(docMapping)) {
    const list = mockDataTyped[key]?.items || [];
    for (const item of list) {
      // Check explicit tags
      if (Array.isArray(item.tags) && item.tags.includes(tagName)) {
        embeddings.push({
          document: { title: docInfo.title, type: docInfo.type },
          entity: { id: item.id, name: item.name },
          field: { id: 'tags' },
        });
      }
      if (Array.isArray(item.include?.tags) && item.include.tags.includes(tagName)) {
        embeddings.push({
          document: { title: docInfo.title, type: docInfo.type },
          entity: { id: item.id, name: item.name },
          field: { id: 'include' },
        });
      }
      if (Array.isArray(item.exclude?.tags) && item.exclude.tags.includes(tagName)) {
        embeddings.push({
          document: { title: docInfo.title, type: docInfo.type },
          entity: { id: item.id, name: item.name },
          field: { id: 'exclude' },
        });
      }

      // Check automatic tags
      const automaticTags: { tag: string; fieldId: string }[] = [];
      const nameTag = normalize(item.name);
      if (key === 'rateLimitRules') {
        automaticTags.push({ tag: `limit-name:${nameTag}`, fieldId: 'name' });
      } else if (key === 'aclProfiles') {
        automaticTags.push({ tag: `aclname:${nameTag}`, fieldId: 'name' });
      } else if (key === 'contentFilterProfiles') {
        automaticTags.push({ tag: `contentfiltername:${nameTag}`, fieldId: 'name' });
      } else if (key === 'contentFilterRules') {
        automaticTags.push({ tag: `cf-rule-name:${nameTag}`, fieldId: 'name' });
        if (item.category) {
          automaticTags.push({
            tag: `cf-rule-category:${normalize(item.category)}`,
            fieldId: 'category',
          });
        }
        if (item.subcategory) {
          automaticTags.push({
            tag: `cf-rule-subcategory:${normalize(item.subcategory)}`,
            fieldId: 'subcategory',
          });
        }
      } else if (key === 'dynamicRules') {
        automaticTags.push({ tag: `dynamic-rule:${nameTag}`, fieldId: 'name' });
      } else if (key === 'edgeFunctions') {
        automaticTags.push({ tag: `edge-function-name:${nameTag}`, fieldId: 'name' });
      } else if (key === 'flowControlPolicies') {
        automaticTags.push({ tag: `fc-name:${nameTag}`, fieldId: 'name' });
      } else if (key === 'securityPolicies') {
        automaticTags.push({ tag: `securitypolicy:${nameTag}`, fieldId: 'name' });
      }

      for (const auto of automaticTags) {
        if (auto.tag === tagName) {
          embeddings.push({
            document: { title: docInfo.title, type: docInfo.type },
            entity: { id: item.id, name: item.name },
            field: { id: auto.fieldId },
          });
        }
      }
    }
  }

  return {
    type: ['user'],
    embeddings,
  };
};

export default {
  shouldUseMockData,
  getAiManagementMockData,
  getAiCrawlersMockData,
  getDashboardMockData,
  getQuarantinedMockData,
  getDynamicRulesMockData,
  getActionsMockData,
  getEventsLogMockData,
  getSslLoadBalancersMockData,
  getSslLoadBalancersRegionsMockData,
  getCertificatesMockData,
  getRateLimitRulesMockData,
  getSecurityPoliciesMockData,
  getMockTagData,
  getAllMockData,
};
